import React, { useEffect } from 'react';
import { Input, Select } from '@attentive-platform/stem-ui';

import { ModifierInputStyles as S } from './styles';

const Chip = ({ label, on, onClick, disabled }) => {
  if (disabled) return <S.ChipDisabled>{label}</S.ChipDisabled>;
  const Cmp = on ? S.ChipOn : S.ChipOff;
  return <Cmp onClick={onClick}>{label}</Cmp>;
};

const optionList = modifier => modifier.answer_options || modifier.modifier_answer_options || [];

const isChoiceModifier = modifier =>
  modifier.modifier_type === 'multi_select' || modifier.modifier_type === 'dropdown';

const ModifierInput = ({ modifier, value, onChange, readOnly }) => {
  const answers = value || { selected_options: [], text_value: '' };
  const options = optionList(modifier);
  const isChoice = isChoiceModifier(modifier);
  const lockedSingle = isChoice && options.length === 1;
  const defaultOption = isChoice && options.length > 0 ? options[0] : null;
  const storedOptions = answers.selected_options || [];
  const selectedOptions =
    lockedSingle && defaultOption != null
      ? [defaultOption]
      : storedOptions.length > 0
        ? storedOptions
        : value == null && defaultOption != null
          ? [defaultOption]
          : storedOptions;

  const setOptions = next => onChange({ selected_options: next });
  const setText = text => onChange({ text_value: text });

  const selectedKey = storedOptions.join('\0');

  // Default the first option into draft when unset; single-option stays forced on.
  useEffect(() => {
    if (readOnly || defaultOption == null) return;
    if (lockedSingle) {
      if (selectedKey === String(defaultOption)) return;
      onChange({ selected_options: [defaultOption] });
      return;
    }
    if (value != null) return;
    onChange({ selected_options: [defaultOption] });
  }, [readOnly, defaultOption, lockedSingle, selectedKey, value, onChange]);

  const bodyParts = (
    <S.Section>
      <S.HeaderRow>
        <S.Title>{modifier.title}</S.Title>
        {modifier.complexity === 'advanced' && (
          <S.AdvancedPill>advanced — may affect turnaround &amp; price</S.AdvancedPill>
        )}
      </S.HeaderRow>
      {modifier.body && <S.BodyText>{modifier.body}</S.BodyText>}
    </S.Section>
  );

  if (modifier.modifier_type === 'multi_select') {
    return (
      <S.Section>
        {bodyParts}
        <S.ChipRow>
          {options.map(opt => {
            const on = selectedOptions.includes(opt);
            return (
              <Chip
                key={opt}
                label={opt}
                on={on}
                disabled={readOnly}
                onClick={
                  readOnly || lockedSingle
                    ? undefined
                    : () => {
                        const next = on
                          ? selectedOptions.filter(x => x !== opt)
                          : [...selectedOptions, opt];
                        setOptions(next);
                      }
                }
              />
            );
          })}
        </S.ChipRow>
      </S.Section>
    );
  }

  if (modifier.modifier_type === 'dropdown') {
    const items = options.map(o => ({ label: o, value: o }));
    const selected = selectedOptions[0] || '';
    return (
      <S.Section>
        {bodyParts}
        <S.SelectWrap>
          <Select
            size='small'
            variant='std-filled'
            items={items}
            value={selected}
            onChange={v => setOptions([v])}
            disabled={readOnly || lockedSingle}
            placeholder='Choose one'
          />
        </S.SelectWrap>
      </S.Section>
    );
  }

  if (modifier.modifier_type === 'subjective') {
    return (
      <S.Section>
        {bodyParts}
        <S.InputWrap>
          <Input
            size='large'
            multiline
            minRows={3}
            value={answers.text_value}
            onChange={e => setText(e.target.value)}
            placeholder='Type your answer'
            disabled={readOnly}
          />
        </S.InputWrap>
      </S.Section>
    );
  }

  if (modifier.modifier_type === 'example') {
    // Scope items render examples via ExampleCards; keep a minimal fallback here.
    return bodyParts;
  }

  return bodyParts;
};

export default ModifierInput;
